import { useMemo, useState } from 'react';

// Form AVANZATO del profilo investitore — le 7 grandezze della specifica
// docs/PROFILO_INVESTITORE.md (Michael 2026-08-07). Regole di design:
// - mai aggettivi astratti: domande a scenario, opzioni auto-esplicative
// - scelta SINGOLA ovunque (soglie monotone / vincolo-o-preferenza / lenti)
// - esempio dinamico sotto ROI/reddito: è la spiegazione del significato
// Usato dallo step 0 del tutorial; riutilizzabile nel tab Profilo.

const FONT = 'Manrope, system-ui, sans-serif';

const S = {
  section: { marginTop: 18 },
  label:   { fontSize: 14.5, fontWeight: 800, color: '#1F2937', marginBottom: 4 },
  hint:    { fontSize: 12, color: '#6B7280', lineHeight: 1.4, marginBottom: 9 },
  chips:   { display: 'flex', flexWrap: 'wrap', gap: 7 },
  example: { marginTop: 8, fontSize: 12, color: '#16915E', background: '#E9F7EF', borderRadius: 9, padding: '7px 10px', lineHeight: 1.4, fontWeight: 600 },
};

function Chip({ active, onClick, children, block }) {
  return (
    <button onClick={onClick} style={{
      border: active ? '1.5px solid #1F6FEB' : '1.5px solid #E5E7EB',
      background: active ? '#EAF2FF' : '#fff',
      color: active ? '#1553C7' : '#374151',
      borderRadius: 11, padding: block ? '11px 12px' : '8px 12px',
      fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
      textAlign: 'left', lineHeight: 1.3,
      ...(block ? { width: '100%', display: 'block' } : {}),
    }}>{children}</button>
  );
}

const euro = (n) => '€ ' + Number(n).toLocaleString('it-IT');

export const PROFILO_DEFAULT = {
  obiettivo: null,           // 'rivendita' | 'reddito' | 'entrambe'
  roi_min_pct: 25,           // lente rivendita
  roi_min_abs: 30000,
  rendimento_min_pct: 6,     // lente reddito
  canone_min_mese: 600,
  orizzonte: null,           // '12' | '24' | '36+' (mesi max)
  mutuo: null,               // 'contanti' | 'necessario' | 'preferito'
  regime_acquisto: null,     // 'prima_casa' | 'seconda_casa' | 'azienda' — regime fiscale d'acquisto (cambia registro/ROI)
  impegno: null,             // 'zero' | 'piccoli' | 'ristrutturazione' | 'imprenditoriale'
  accetta_incertezza: null,  // bool — accetti un'incognita non risolvibile prima dell'asta?
  // NB1: il capitale disponibile NON è nel profilo (è un vincolo/filtro di ricerca,
  //      già coperto da "Prezzo massimo"). Michael 2026-08-07.
  // NB2: rischio SEMPLIFICATO a un binario (accetti l'incertezza sì/no). Niente 3 livelli
  //      né percentuali: complicavano senza che l'utente capisse. Michael 2026-08-07.
};

export default function ProfiloInvestitoreForm({ initial, onSave, onBack }) {
  const [p, setP] = useState({ ...PROFILO_DEFAULT, ...(initial || {}) });
  // Persisti una BOZZA a ogni tap (Michael 2026-08-10): così le scelte parziali non vanno
  // perse quando chiudi il form senza aver completato tutte le 8 → al riapri le rivedi selezionate.
  const set = (k, v) => setP((prev) => {
    const next = { ...prev, [k]: v };
    try { localStorage.setItem('rivelasta_profilo_avanzato_draft', JSON.stringify(next)); } catch { /* noop */ }
    return next;
  });

  const lenteFlip    = p.obiettivo === 'rivendita' || p.obiettivo === 'entrambe';
  const lenteReddito = p.obiettivo === 'reddito'   || p.obiettivo === 'entrambe';

  // Esempio dinamico ROI: investimento tipo 160k → prezzo di rivendita minimo
  const esempioRoi = useMemo(() => {
    const invest = 160000;
    const target = Math.max(invest * (1 + p.roi_min_pct / 100), invest + p.roi_min_abs);
    return `Es.: un'operazione dove investi ${euro(invest)} tutto compreso ti interessa solo se rivendi ad almeno ${euro(Math.round(target / 1000) * 1000)}.`;
  }, [p.roi_min_pct, p.roi_min_abs]);

  const esempioReddito = useMemo(() => (
    `Es.: un immobile che affitti a ${euro(p.canone_min_mese)}/mese deve costarti al massimo ${euro(Math.round((p.canone_min_mese * 12) / (p.rendimento_min_pct / 100) / 1000) * 1000)} tutto compreso.`
  ), [p.rendimento_min_pct, p.canone_min_mese]);

  const completo = p.obiettivo && p.orizzonte && p.mutuo && p.regime_acquisto && p.impegno
    && p.accetta_incertezza !== null;

  return (
    <div style={{ fontFamily: FONT }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={onBack} style={{ border: 0, background: 'transparent', color: '#1F6FEB', fontWeight: 800, fontSize: 13.5, cursor: 'pointer', padding: '4px 6px 4px 0' }}>‹ Indietro</button>
        <div style={{ fontSize: 17, fontWeight: 900, color: '#1F2937' }}>Profilo avanzato</div>
      </div>
      <div style={{ fontSize: 12.5, color: '#6B7280', lineHeight: 1.4, marginTop: 3 }}>
        Otto scelte, tutte concrete: ogni verdetto sarà calibrato su queste risposte.
      </div>

      {/* 1. Obiettivo (lente) */}
      <div style={S.section}>
        <div style={S.label}>1 · Cosa vuoi fare con l'immobile?</div>
        <div style={S.chips}>
          <Chip active={p.obiettivo === 'rivendita'} onClick={() => set('obiettivo', 'rivendita')}>Rivendere</Chip>
          <Chip active={p.obiettivo === 'reddito'} onClick={() => set('obiettivo', 'reddito')}>Mettere a reddito</Chip>
          <Chip active={p.obiettivo === 'entrambe'} onClick={() => set('obiettivo', 'entrambe')}>Sono aperto a entrambe</Chip>
        </div>
      </div>

      {/* 2. ROI (lente rivendita) */}
      {lenteFlip && (
        <div style={S.section}>
          <div style={S.label}>2 · Guadagno minimo sulla rivendita</div>
          <div style={S.hint}>Contano entrambi: la percentuale E la cifra assoluta.</div>
          <div style={{ ...S.chips, marginBottom: 7 }}>
            {[15, 25, 40, 60].map((v) => (
              <Chip key={v} active={p.roi_min_pct === v} onClick={() => set('roi_min_pct', v)}>≥ {v}%</Chip>
            ))}
          </div>
          <div style={S.chips}>
            {[10000, 30000, 50000, 100000].map((v) => (
              <Chip key={v} active={p.roi_min_abs === v} onClick={() => set('roi_min_abs', v)}>≥ {euro(v)}</Chip>
            ))}
          </div>
          <div style={S.example}>{esempioRoi}</div>
        </div>
      )}

      {/* 3. Reddito (lente reddito) */}
      {lenteReddito && (
        <div style={S.section}>
          <div style={S.label}>{lenteFlip ? '3' : '2'} · Rendita minima dall'affitto</div>
          <div style={S.hint}>Rendimento lordo annuo E canone mensile minimo.</div>
          <div style={{ ...S.chips, marginBottom: 7 }}>
            {[4, 6, 8, 10].map((v) => (
              <Chip key={v} active={p.rendimento_min_pct === v} onClick={() => set('rendimento_min_pct', v)}>≥ {v}% lordo</Chip>
            ))}
          </div>
          <div style={S.chips}>
            {[400, 600, 900, 1500].map((v) => (
              <Chip key={v} active={p.canone_min_mese === v} onClick={() => set('canone_min_mese', v)}>≥ {euro(v)}/mese</Chip>
            ))}
          </div>
          <div style={S.example}>{esempioReddito}</div>
        </div>
      )}

      {/* 4. Orizzonte */}
      <div style={S.section}>
        <div style={S.label}>Quando vuoi vedere il ritorno?</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <Chip block active={p.orizzonte === '12'} onClick={() => set('orizzonte', '12')}>
            <b>Entro 1 anno</b> — solo immobili liberi o quasi, niente pratiche lunghe
          </Chip>
          <Chip block active={p.orizzonte === '24'} onClick={() => set('orizzonte', '24')}>
            <b>1-2 anni</b> — accetto liberazione dell'immobile e lavori
          </Chip>
          <Chip block active={p.orizzonte === '36+'} onClick={() => set('orizzonte', '36+')}>
            <b>Oltre 2 anni</b> — accetto anche situazioni complesse da sbrogliare
          </Chip>
        </div>
      </div>

      {/* 5. Mutuo */}
      <div style={S.section}>
        <div style={S.label}>Come paghi?</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <Chip block active={p.mutuo === 'contanti'} onClick={() => set('mutuo', 'contanti')}>
            <b>Solo contanti</b> — nessun vincolo bancario
          </Chip>
          <Chip block active={p.mutuo === 'necessario'} onClick={() => set('mutuo', 'necessario')}>
            <b>Mi serve il mutuo</b> — escluderemo gli immobili che le banche non finanziano
          </Chip>
          <Chip block active={p.mutuo === 'preferito'} onClick={() => set('mutuo', 'preferito')}>
            <b>Preferirei il mutuo, ma posso farne a meno</b> — daremo priorità ai finanziabili
          </Chip>
        </div>
      </div>

      {/* 5b. Regime di acquisto (fiscale) */}
      <div style={S.section}>
        <div style={S.label}>Come compri?</div>
        <div style={S.hint}>Cambia le imposte d'acquisto, quindi il ROI reale dell'operazione.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <Chip block active={p.regime_acquisto === 'prima_casa'} onClick={() => set('regime_acquisto', 'prima_casa')}>
            <b>Prima casa</b> — ci vado a vivere (registro 2% sul valore catastale)
          </Chip>
          <Chip block active={p.regime_acquisto === 'seconda_casa'} onClick={() => set('regime_acquisto', 'seconda_casa')}>
            <b>Seconda casa</b> — investo come privato (registro 9%)
          </Chip>
          <Chip block active={p.regime_acquisto === 'azienda'} onClick={() => set('regime_acquisto', 'azienda')}>
            <b>Con una società</b> — acquisto tramite azienda di rivendita
          </Chip>
        </div>
      </div>

      {/* 6. Impegno */}
      <div style={S.section}>
        <div style={S.label}>Quanto lavoro sei disposto a gestire?</div>
        <div style={S.hint}>Scegli il massimo che accetteresti: i livelli sotto sono inclusi.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <Chip block active={p.impegno === 'zero'} onClick={() => set('impegno', 'zero')}>
            <b>Chiavi in mano</b> — compro e basta
          </Chip>
          <Chip block active={p.impegno === 'piccoli'} onClick={() => set('impegno', 'piccoli')}>
            <b>Piccoli lavori</b> — tinteggi, un bagno, dettagli
          </Chip>
          <Chip block active={p.impegno === 'ristrutturazione'} onClick={() => set('impegno', 'ristrutturazione')}>
            <b>Ristrutturazione completa</b> — cantiere da gestire, con un'impresa
          </Chip>
          <Chip block active={p.impegno === 'imprenditoriale'} onClick={() => set('impegno', 'imprenditoriale')}>
            <b>Operazioni imprenditoriali</b> — frazionamenti, cambi d'uso, sanatorie multiple
          </Chip>
        </div>
      </div>

      {/* 7. Rischio — una sola domanda binaria (Michael 2026-08-07) */}
      <div style={S.section}>
        <div style={S.label}>E se c'è un'incognita?</div>
        <div style={S.hint}>
          A volte l'affare migliore ha un'incognita che non si può sciogliere prima
          dell'asta (una pratica dall'esito incerto, uno stato non verificabile).
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <Chip block active={p.accetta_incertezza === true} onClick={() => set('accetta_incertezza', true)}>
            <b>Me la gioco</b> — accetto l'incertezza se il potenziale ne vale la pena
          </Chip>
          <Chip block active={p.accetta_incertezza === false} onClick={() => set('accetta_incertezza', false)}>
            <b>Voglio certezze</b> — solo affari senza incognite aperte
          </Chip>
        </div>
      </div>

      <button disabled={!completo} onClick={() => { try { localStorage.removeItem('rivelasta_profilo_avanzato_draft'); } catch { /* noop */ } onSave(p); }} style={{
        marginTop: 20, width: '100%', border: 0, borderRadius: 13, padding: '13px',
        fontWeight: 800, fontSize: 15, cursor: completo ? 'pointer' : 'default',
        background: completo ? '#16915E' : '#E5E7EB', color: completo ? '#fff' : '#9CA3AF',
        fontFamily: FONT,
      }}>
        {completo ? 'Salva il mio profilo' : 'Completa tutte le scelte per salvare'}
      </button>
    </div>
  );
}
